import { Fragment, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import type { PartialSaveOptions } from 'payments/types/form';
import { useAccessToken } from 'reservations/account/providers/AccessTokenProvider';
import { useLocationId } from 'reservations/account/providers/ConfigProvider';
import { trackCreateBooking } from 'reservations/analytics/helpers';
import BaseButton from 'reservations/base/components/BaseButton';
import { NEXTABLE_ERROR_CODE } from 'reservations/base/constants/nextable';
import { useErrorSnackbar } from 'reservations/base/hooks/useSnackbar';
import { useLoadingSpinner } from 'reservations/base/providers/LoadingSpinnerProvider';
import { useCustomRouter } from 'reservations/base/providers/RouterProvider';
import {
  BookingStateEnum,
  type HandleCreateBookingSuccessOptions,
  type HandleErrorOptions,
  type HandleUpdateBookingSuccessOptions
} from 'reservations/booking/types';
import { useDinerAccount } from 'reservations/dinerAccount/hooks/useDinerAccount';
import { useIsSubjectToCancellationFee } from 'reservations/feePolicy/hooks';
import { updateMarketingConsent } from 'reservations/form/queries/updateMarketingConsent';
import { useCreateBookingMutation } from 'reservations/form/queries/useCreateBookingMutation';
import { useUpdateBookingMutation } from 'reservations/form/queries/useUpdateBookingMutation';
import { RequiredPayment } from 'reservations/payments/components/RequiredPayment';
import { useDinerPaymentMethods } from 'reservations/payments/hooks/useDinerPaymentMethods';
import { usePaymentProviderSubmission } from 'reservations/payments/hooks/usePaymentProviderSubmission';
import { usePreviewModeContext } from 'reservations/preview/providers/PreviewModeProvider';

interface CreateReservationButtonProps {
  handleFormDrawerClose: () => void;
}

const CreateReservationButton = ({
  handleFormDrawerClose
}: CreateReservationButtonProps) => {
  const partySize = useWatch({
    name: 'partySize'
  });
  const isSubjectToCancellationFee = useIsSubjectToCancellationFee(partySize);
  const ButtonWrapper = isSubjectToCancellationFee ? RequiredPayment : Fragment;

  return (
    <ButtonWrapper>
      <InternalCreateReservationButton
        handleFormDrawerClose={handleFormDrawerClose}
        isSubjectToCancellationFee={isSubjectToCancellationFee}
      />
    </ButtonWrapper>
  );
};

interface InternalCreateReservationButtonProps
  extends CreateReservationButtonProps {
  isSubjectToCancellationFee: boolean;
}

const InternalCreateReservationButton = ({
  handleFormDrawerClose,
  isSubjectToCancellationFee
}: InternalCreateReservationButtonProps) => {
  const { isPreview } = usePreviewModeContext();
  const { handleHasLoaded, handleIsLoading } = useLoadingSpinner();
  const { handleSubmit } = useFormContext();
  const [
    availability,
    optInChecklessPayments,
    savedPaymentMethod,
    tipPercentage
  ] = useWatch({
    name: [
      'availability',
      'optInChecklessPayments',
      'savedPaymentMethod',
      'tipPercentage'
    ]
  });
  const { handleNavigation } = useCustomRouter();
  const enqueueErrorSnackbar = useErrorSnackbar();
  const { access_token: apiKey, slug } = useAccessToken();
  const locationId = useLocationId();
  const { shouldAutoEnroll } = useDinerAccount();
  const { defaultPaymentMethodId } = useDinerPaymentMethods();
  // FIXME: Add test in DMD-4776
  // istanbul ignore next
  const onSuccess = () => {
    handleFormDrawerClose();
    handleHasLoaded();
  };

  const handlePaymentProviderSubmission =
    usePaymentProviderSubmission(onSuccess);

  const [pendingCreatedBooking, setPendingCreatedBooking] =
    useState<PartialSaveOptions | null>(null);

  // NOTE: handleSuccess is a custom callback that we use to close the modal
  // istanbul ignore next
  const handleSuccess = ({
    navUrl,
    booking
  }: HandleCreateBookingSuccessOptions | HandleUpdateBookingSuccessOptions) => {
    // submit payment form if the booking is subject to cancellation fee is in a pending state,
    // or if the diner wants to use checkless payments

    const shouldSaveCardForCancellationFee =
      isSubjectToCancellationFee &&
      booking.state === BookingStateEnum.enum.Pending;
    const shouldSaveCardOnFile =
      shouldAutoEnroll || shouldSaveCardForCancellationFee;

    updateMarketingConsent(booking);

    // istanbul ignore next
    if (shouldSaveCardOnFile) {
      const payload: PartialSaveOptions = {
        locationId,
        bookingId: booking.bookingId,
        email: booking.email,
        firstName: booking.firstName,
        lastName: booking.lastName,
        phone: booking.phone,
        phoneCode: booking.phoneCode,
        shouldConfirmCardWithNextable: shouldSaveCardForCancellationFee,
        optInChecklessPayments,
        tipPercentage,
        ...(savedPaymentMethod && { savedPaymentMethod })
      };

      /* If we're auto-enrolling diner and they're not subject to a cancellation fee we should use their default payment method
      which for clover is just the card they have saved. If we're auto-enrolling and they are subject to cancellation fee,
      we should defer to whichever payment method they add or select during the reservation flow */
      if (shouldAutoEnroll && !isSubjectToCancellationFee) {
        payload.savedPaymentMethod = defaultPaymentMethodId;
      }

      setPendingCreatedBooking(payload);
      handlePaymentProviderSubmission(payload);
    } else {
      // NOTE: On a successful creation of a booking, we want to clear out the pending booking
      setPendingCreatedBooking(null);
      onSuccess();

      if (navUrl) {
        handleNavigation(navUrl);
      }
    }
  };

  // istanbul ignore next
  const handleError = ({
    navUrl,
    code,
    forceReload = false
  }: HandleErrorOptions) => {
    handleHasLoaded();

    if (code === NEXTABLE_ERROR_CODE.NO_AVAILABILITY) {
      enqueueErrorSnackbar(
        'CREATE_BOOKING_ERROR_SNACKBAR',
        `${availability} is no longer available. Please select a new time.`
      );
    }

    if (navUrl || forceReload) {
      handleNavigation(navUrl, forceReload);
    }
  };

  const createBookingMutation = useCreateBookingMutation({
    slug,
    apiKey,
    handleSuccess,
    handleError
  });

  const updateBookingMutation = useUpdateBookingMutation({
    slug,
    apiKey,
    bookingId: pendingCreatedBooking?.bookingId ?? '',
    handleSuccess,
    handleError
  });

  // istanbul ignore next
  const onSubmit = (data: Record<string, any>) => {
    handleIsLoading('Booking your reservation...');

    // If the booking already exists, then we should update the booking instead
    if (pendingCreatedBooking !== null) {
      updateBookingMutation.mutate(data);
    } else {
      trackCreateBooking(data);
      createBookingMutation.mutate(data);
    }
  };

  return (
    <BaseButton
      label="Reserve"
      onClick={handleSubmit(onSubmit)}
      disabled={isPreview}
    />
  );
};

export default CreateReservationButton;
